import { AxiosResponse } from 'axios';
import { HttpClient } from '../shared/utils';
import { COOKIE_NAMES, LocalStorageUtil } from './../utils/local-storage';
import { LoginAuthorization } from '../models/login-authorization';
import { RegisteredUser } from '../models/registered-user';
import LoginProvider, { CC_APP_TYPE } from '../providers/login.provider';
import Bugsnag from '@bugsnag/js';

// let singletonInstance: HttpConnectorAdapter;
let registeredUser: RegisteredUser | undefined = undefined as RegisteredUser | undefined;
const loginProvider = new LoginProvider();

interface StandardHTTPHeaders {
    'X-Username': string;
    Authorization: string;
    userId: string;
    authToken: string;
}


export class HttpConnectorAdapter {
    // static post(url: string, data?: any, headers?: any): Promise<AxiosResponse> {
    //     return HttpClient.post(url, data, headers);
    // }

    // static get(url: string, params?: any, headers?: any): Promise<AxiosResponse> {
    //     return HttpClient.get(url, params, headers);
    // }

    static checkCookieExpire(requestHeaders?: any): Promise<StandardHTTPHeaders | AxiosResponse> {
        const user_cookie = LocalStorageUtil.getItem(COOKIE_NAMES.REGISTERED_USER) as string;
        const auth_cookie = LocalStorageUtil.getItem(COOKIE_NAMES.LOGIN_AUTHORIZATION) as string;

        const registeredUser = RegisteredUser.createFromSerializedData(
            user_cookie
        )!;
        const authData = LoginAuthorization.createFromSerializedData(auth_cookie);
        const {
            cookie,
            isSSO,
            userId,
            accountId
        } = registeredUser;
        
        if (!isSSO || !cookie || !authData) {
            const error: AxiosResponse = {
                data: {
                    message: 'Internal Server Error',
                    code: 'INVALID SSO DATA'
                },
                status: 500,
                statusText: 'Internal Server Error',
                headers: {
                    'content-type': 'application/json'
                },
                config: {},
                request: {}
            };
            return Promise.reject(error);
        }
        
        const { Authorization } = cookie;
        const headers = Object.assign({}, requestHeaders, {
            Authorization,
            cctype: CC_APP_TYPE,
            accountId,
            userId
        });
        
        if (!this.isSSOTimeoutExpired(registeredUser)) {
            return Promise.resolve(headers);
        }

        return loginProvider.refreshSSOToken(authData.validationId!).then(response => {
            const {
                expiration: expirationStringUTC,
                access_token: accessToken,
                id_token: idToken
            } = response;
            
            if (!idToken) {
                registeredUser.updateAccessToken(accessToken);
            } else {
                registeredUser.updateAccessAndIdToken(accessToken, idToken);
            }

            registeredUser.expiration = expirationStringUTC ? new Date(expirationStringUTC) : undefined;

            LocalStorageUtil.setItem(
                COOKIE_NAMES.REGISTERED_USER,
                registeredUser.serialize()
            );


            return headers;
        }).catch(error => {
            Bugsnag.notify((new Error('CheckSSORefreshToken - Refresh token has an error')), (event) => {
                event.severity = 'error';
                event.context = 'SSO Refresh Token Actions';
                event.addMetadata('sso_check_refresh_token', {
                    validation_id: authData.validationId!,
                    registered_user: registeredUser,
                    error: error
                });
            });

            return error as AxiosResponse;
        });
    }

    static getWithCredentials(url: string, params?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.get(url, params, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static getFileWithCredentials(url: string, params?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.getBlob(url, params, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static putWithCredentials(url: string, params?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.put(url, params, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static deleteWithCredentials(url: string, params?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.delete(url, params, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static postWithCredentials(url: string, data?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.post(url, data, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static postFileWithCredentials(url: string, data?: any, headers?: any): Promise<AxiosResponse> {
        return new Promise ((resolve, reject) => {
            this.checkCookieExpire(headers).then(authHeaders => {
                return resolve(HttpClient.postBlob(url, data, authHeaders));
            }).catch(error => {
                return reject(error);
            })
        });
    }

    static setRegisteredUser(newRegisteredUser: RegisteredUser | undefined): void {
        registeredUser = newRegisteredUser;
        if (!registeredUser) {
            return;
        }
    }

    static isSSOTimeoutExpired(checkUser: RegisteredUser): boolean {
        if (!checkUser ||
            !(checkUser.valid) ||
            !(checkUser.isSSO) ||
            !(checkUser.expiration)
        ) {
            return false;
        }

        // Get the expiration converted to local time
        const expiration = checkUser.expiration;
        
        return expiration.getTime() <= Date.now();
    }
}